// MRP for make items (ERP-PLAN §5.4 "Run", steps 2–4), used by planPurchasing() in shared/planning.ts:
// - which items are built at a contract manufacturer (a BOM family whose current revision names a make vendor)
//   and what one unit takes from HHL, through the BOMs (a sub-assembly built in the same order exploded into its parts,
//   one another maker builds kept as an item of its own that's planned in turn, scrap included); parts the CM buys and
//   services are never planned. A sub-assembly built in the same order uses its own stock first (`builtIn`: planning
//   takes what's in stock before its parts)
// - the order items are planned in: low-level codes, so an item is planned after everything that uses it and
//   gets all of its demand (a joystick sold on its own and used in a GCU 2 adds both)
// - pegging: which demand each planned order is for, back to the sell items and the days they need it.
// planPurchasing() explodes each build into its parts' demand on the day the build starts, so each level is
// offset by the lead times above it (the CM's build time, then the part's own lead time).
import { type BomIndex, type BomItem, type BomRow, byCm, families, flatten, resolve, variantsOf } from "./boms.ts";

export type MakePart = { itemId: string; item: BomItem; qty: number };

export type MakeSpec = {
  itemId: string;
  bom: BomRow;
  /** The contract manufacturer that builds it (bom.make_vendor_id). */
  vendorId: string;
  /** Per unit, what HHL buys and consigns to the CM (scrap and phantom parents included). */
  parts: MakePart[];
  /** Per unit, what the CM buys into its price: shown, never planned. */
  cmBuys: MakePart[];
  /**
   * Sub-assemblies built in the same order (in `parts` as their parts): per unit, and the parts HHL sends per one of
   * them, so planning can use their own stock first and plan only the rest's parts.
   */
  builtIn: { itemId: string; qty: number; parts: { itemId: string; qty: number }[] }[];
  warnings: string[];
};

export type BomGraph = {
  /** Items built at a CM, by item id (a product's variants, or a stocked sub-assembly). */
  make: Map<string, MakeSpec>;
  /** Low-level code of every item MRP plans: 0 = nothing uses it; a part is one more than the deepest item using it. */
  level: Map<string, number>;
  /** The make items each part goes into directly. */
  parents: Map<string, string[]>;
};

const graphs = new WeakMap<BomIndex, BomGraph>();

/** A build's sub-assemblies built in the same order (top level), with the parts HHL sends for one of each. */
function builtInOf(r: ReturnType<typeof resolve>): MakeSpec["builtIn"] {
  const out: MakeSpec["builtIn"] = [];
  for (const h of r.lines) {
    if (!h.phantom || h.depth !== 0 || h.item.source !== "local" || h.qty <= 0) continue;
    const parts = new Map<string, number>();
    for (const l of r.lines) {
      if (l.phantom || !l.key.startsWith(`${h.key}/`) || l.item.kind === "service" || byCm(l.supply)) continue;
      parts.set(l.itemId, (parts.get(l.itemId) ?? 0) + l.qty / h.qty);
    }
    out.push({ itemId: h.itemId, qty: h.qty, parts: [...parts].map(([itemId, qty]) => ({ itemId, qty })) });
  }
  return out;
}
const MAX_LEVELS = 30;

/** The make items, their parts and the planning order, worked out once per BOM index. */
export function bomGraph(ix: BomIndex): BomGraph {
  const hit = graphs.get(ix);
  if (hit) return hit;
  const make = new Map<string, MakeSpec>();
  for (const bom of families(ix)) {
    if (!bom.make_vendor_id || bom.phantom) continue;
    for (const variant of variantsOf(ix, bom)) {
      if (variant.archived) continue;
      const resolved = resolve(ix, bom, variant);
      const parts: MakePart[] = [];
      const cmBuys: MakePart[] = [];
      for (const row of flatten(resolved)) {
        if (row.item.kind === "service" || row.qty <= 0 || row.item.id === variant.id) continue;
        (byCm(row.supply) ? cmBuys : parts).push({ itemId: row.item.id, item: row.item, qty: row.qty });
      }
      if (!parts.length && !cmBuys.length) continue; // a BOM with nothing in it yet
      make.set(variant.id, { itemId: variant.id, bom, vendorId: bom.make_vendor_id, parts, cmBuys, builtIn: builtInOf(resolved), warnings: resolved.warnings });
    }
  }
  const level = new Map<string, number>();
  const parents = new Map<string, string[]>();
  for (const [id, spec] of make) {
    if (!level.has(id)) level.set(id, 0);
    for (const p of spec.parts) {
      if (!level.has(p.itemId)) level.set(p.itemId, 0);
      const list = parents.get(p.itemId);
      if (list) list.push(id);
      else parents.set(p.itemId, [id]);
    }
  }
  // A part sits one level below the deepest item that uses it (relaxed until nothing moves; a loop stops at the cap).
  for (let pass = 0; pass < MAX_LEVELS; pass++) {
    let moved = false;
    for (const [id, spec] of make) {
      const below = Math.min(MAX_LEVELS, (level.get(id) ?? 0) + 1);
      for (const p of spec.parts) {
        if ((level.get(p.itemId) ?? 0) < below) {
          level.set(p.itemId, below);
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  const graph = { make, level, parents };
  graphs.set(ix, graph);
  return graph;
}

/**
 * The graph's low-level codes with more edges (open make orders' BOM snapshots, which can name parts the current
 * revision dropped): every item in either, a part below the deepest item using it. The graph's own map when none.
 */
export function planLevels(graph: BomGraph, extra: readonly { parent: string; parts: readonly string[] }[]): Map<string, number> {
  const edges = extra.filter((e) => e.parts.length);
  if (!edges.length) return graph.level;
  const level = new Map(graph.level);
  for (const e of edges) {
    if (!level.has(e.parent)) level.set(e.parent, 0);
    for (const p of e.parts) if (!level.has(p)) level.set(p, 0);
  }
  const all = [...[...graph.make.values()].map((m) => ({ parent: m.itemId, parts: m.parts.map((p) => p.itemId) })), ...edges];
  for (let pass = 0; pass < MAX_LEVELS; pass++) {
    let moved = false;
    for (const e of all) {
      const below = Math.min(MAX_LEVELS, (level.get(e.parent) ?? 0) + 1);
      for (const p of e.parts) {
        if ((level.get(p) ?? 0) < below) {
          level.set(p, below);
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return level;
}

// Demand from builds and pegging -------------------------------------------------------------------------

/** A sell item (or the top item) a need is ultimately for: `qty` of it at HHL between projection days `from` and `to`. */
export type PegRoot = { itemId: string; from: number; to: number; qty: number };

/** One build above a part: the item built, where, and its build time. */
export type ChainStep = { itemId: string; vendorId: string; lead: number; shipTo: string | null };

/** A part's demand from one build (planned, or an open make PO), on the day the build starts. */
export type DependentDemand = {
  /** Projection day it's needed at the CM: the build's start. */
  day: number;
  qty: number;
  /** The item being built and the CM building it. */
  parentId: string;
  vendorId: string;
  /** The CM's location (where the part ships). */
  shipTo: string | null;
  buildQty: number;
  buildDay: number;
  /** When the build is due finished (at HHL, or at the next CM up). */
  buildEnd: number;
  /** An open make PO's number; null for one of the plan's builds. */
  poNumber: string | null;
  /** The builds above the part, nearest first: their lead times add up to the part's cumulative lead time. */
  chain: ChainStep[];
  roots: PegRoot[];
};

/** What a planned order (or stock, or an open PO) is for. */
export type Peg = {
  /** reserved: units of a run that aren't for sale (Pre-sales: backers' add-ons, holds). */
  kind: "forecast" | "b2b" | "build" | "reserved";
  /** Projection days the demand falls on. */
  from: number;
  to: number;
  qty: number;
  /** For builds: which one. */
  demand: DependentDemand | null;
  /** Pre-sales --- where reserved units are needed (a CM packing backers' boxes); null: HHL. */
  shipTo?: string | null;
};

export type PegSupply = { key: string; day: number; qty: number };
/** `span`: days the demand is spread over (a week of forecast), so a part covered earlier moves its start. */
export type PegDemand = { kind: Peg["kind"]; day: number; qty: number; demand: DependentDemand | null; span?: number; shipTo?: string | null };

const EPS = 1e-9;

/**
 * Hands each demand to the supply in line for it, first come first served (both in day order): stock on hand,
 * then open POs, then planned orders. Returns what each supply (by key) covers; consecutive forecast weeks merge.
 */
export function peg(supplies: readonly PegSupply[], demands: readonly PegDemand[]): Map<string, Peg[]> {
  const out = new Map<string, Peg[]>();
  const s = supplies.filter((x) => x.qty > EPS).sort((a, b) => a.day - b.day);
  const d = demands.filter((x) => x.qty > EPS).sort((a, b) => a.day - b.day || Number(a.kind !== "build") - Number(b.kind !== "build"));
  let i = 0;
  let left = s[0]?.qty ?? 0;
  for (const dem of d) {
    let need = dem.qty;
    while (need > EPS && i < s.length) {
      const take = Math.min(need, left);
      if (take > EPS) {
        // A week of forecast partly covered already starts later.
        const span = dem.span ?? 1;
        const from = dem.day + Math.floor((span * (dem.qty - need)) / dem.qty + EPS);
        const to = dem.day + Math.max(0, Math.ceil((span * (dem.qty - need + take)) / dem.qty - EPS) - 1);
        const list = out.get(s[i].key) ?? [];
        const last = list.at(-1);
        if (last && last.kind === "forecast" && dem.kind === "forecast") {
          last.to = to;
          last.qty += take;
        } else list.push({ kind: dem.kind, from, to, qty: take, demand: dem.demand, ...(dem.shipTo !== undefined ? { shipTo: dem.shipTo } : {}) });
        out.set(s[i].key, list);
      }
      need -= take;
      left -= take;
      if (left <= EPS) left = s[++i]?.qty ?? 0;
    }
    if (i >= s.length) break;
  }
  return out;
}

/** The sell items a set of pegs is for, one entry per item (its share of a build's roots for parts). */
export function rootsOf(itemId: string, pegs: readonly Peg[]): PegRoot[] {
  const out = new Map<string, PegRoot>();
  const add = (r: PegRoot) => {
    const cur = out.get(r.itemId);
    if (!cur) out.set(r.itemId, { ...r });
    else {
      cur.from = Math.min(cur.from, r.from);
      cur.to = Math.max(cur.to, r.to);
      cur.qty += r.qty;
    }
  };
  for (const p of pegs) {
    if (p.kind !== "build" || !p.demand) add({ itemId, from: p.from, to: p.to, qty: p.qty });
    else {
      const share = p.demand.qty > EPS ? p.qty / p.demand.qty : 0;
      for (const r of p.demand.roots) add({ ...r, qty: r.qty * share });
    }
  }
  return [...out.values()].sort((a, b) => a.from - b.from);
}

/** The longest chain of builds above a set of pegs (what the cumulative lead time adds up). */
export function chainOf(pegs: readonly Peg[]): ChainStep[] {
  let best: ChainStep[] = [];
  let total = -1;
  for (const p of pegs) {
    if (!p.demand) continue;
    const t = p.demand.chain.reduce((n, s) => n + s.lead, 0);
    if (t > total) [best, total] = [p.demand.chain, t];
  }
  return best;
}

/** Weekly totals of a daily demand from day 0 to `days`, as pegging demands on each week's first day. */
export function weeklyDemand(rateOn: (d: number) => number, days: number): PegDemand[] {
  const out: PegDemand[] = [];
  for (let w = 0; w < days; w += 7) {
    let qty = 0;
    for (let d = w; d < Math.min(days, w + 7); d++) qty += rateOn(d);
    if (qty > EPS) out.push({ kind: "forecast", day: w, qty, demand: null, span: Math.min(7, days - w) });
  }
  return out;
}
